import { AxiosError, AxiosResponse } from "axios";
import { Dispatch } from "redux";
import { Api } from "../../../../utils/Api";
import { getRequest } from "../../../../utils/httpRequest";
import { errorNotification } from "../../../../utils/helpers";
import {
  CREATE_POST_SUCCESS,
  CREATE_POST_LOADER,
  UPDATE_POST_LINKS,
  CreatePostSuccessAction,
  CreatePostLoaderAction,
  UpdatePostLinksAction,
  PostActionTypes,
  Post,
} from "../types";

const LINKS_POLL_ATTEMPTS = 5;

export const createPostLoader = (status: boolean): CreatePostLoaderAction => ({
  type: CREATE_POST_LOADER,
  payload: status,
});

export const createPostSuccess = (data: Post): CreatePostSuccessAction => ({
  type: CREATE_POST_SUCCESS,
  payload: data,
});

export const updatePostLinks = (
  postId: number,
  links: Post["links"],
): UpdatePostLinksAction => ({
  type: UPDATE_POST_LINKS,
  postId,
  links,
});

const hasPendingLink = (links: Post["links"] | undefined): boolean =>
  (links || []).some((link) => link.status === "pending");

// Only relevant when a link's eager fetch didn't already resolve it before
// submit — those come back "resolved" (or "failed") immediately. This
// polls purely for the ones the post-save job still has to reconcile.
export const pollForLinks = (
  dispatch: Dispatch<PostActionTypes>,
  postId: number,
  attempt = 0,
): void => {
  if (attempt >= LINKS_POLL_ATTEMPTS) return;

  window.setTimeout(() => {
    getRequest(`/api/community/posts/${postId}.json`)
      .then((response: AxiosResponse<Post>) => {
        dispatch(updatePostLinks(postId, response.data.links));

        if (hasPendingLink(response.data.links)) {
          pollForLinks(dispatch, postId, attempt + 1);
        }
      })
      .catch(() => {});
  }, 1500 * (attempt + 1));
};

export const createPost =
  (content: string, topicUuid?: string, image?: File, linkUrls: string[] = []) =>
  async (dispatch: Dispatch<PostActionTypes>): Promise<void> => {
    dispatch(createPostLoader(true));

    const token = $('meta[name="csrf-token"]').attr("content");
    const formData = new FormData();
    formData.append("community_post[content]", content);
    if (image) formData.append("community_post[images][]", image);
    linkUrls.forEach((linkUrl) => formData.append("community_post[link_urls][]", linkUrl));

    const url = topicUuid
      ? `/api/community/posts.json?topic_uuid=${topicUuid}`
      : `/api/community/posts.json`;

    new Api({})
      .post<Post, FormData>(
        url,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "X-CSRF-Token": token,
          },
        },
      )
      .then((response: AxiosResponse<Post>) => {
        dispatch(createPostSuccess(response.data));

        if (hasPendingLink(response.data.links)) {
          pollForLinks(dispatch, response.data.id);
        }
      })
      .catch((error: AxiosError<{ error: string }>) => {
        errorNotification(
          "Failed to create post",
          JSON.stringify(error?.response?.data),
        );
      })
      .finally(() => dispatch(createPostLoader(false)));
  };
